/* ============================================================================
 * DIGITAL_EMBASSY.EXE — the sovereign pass
 * ----------------------------------------------------------------------------
 * Two things live here and they are deliberately kept apart:
 *
 *   1. THE PASSPORT — the holder's presentation of self. A data page (photo,
 *      declaring fields, machine zone) plus STAMPS. The photograph is the ENS
 *      avatar record; the machine zone is derived from the fields above it with
 *      a real ICAO 9303 check character, not typed in as decoration.
 *
 *   2. THE CREDENTIAL LEDGER — what has actually been attested TO this address,
 *      read live from the EAS indexer. A stamp with no attestation behind it is
 *      not drawn: an empty passport is the honest state, and the desk says so
 *      rather than filling the page with sample rows.
 *
 * Stamps and the ERC-6551 container are different things, and the page keeps
 * them in different tabs so they cannot be read as one: the container is a
 * token-bound account derived from the deployed collection but not created
 * (nothing minted yet), while a stamp is an attestation sitting against the
 * wallet today.
 *
 * READS ONLY. Nothing in this desk signs.
 * ========================================================================= */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useEnsText } from 'wagmi';
import { mainnet } from 'wagmi/chains';
import { useWallet } from '../hooks/useWallet';
import {
  useAttestationCount,
  useAttestationsReceived,
  useSchemaRegistration,
} from '../hooks/useEasReads';
import {
  icaoCheckDigit,
  indexerFor,
  mrzChars,
  shortUid,
  unixToDate,
  type EasRead,
} from '../lib/eas-index';
import { ORG_ENS_NAME } from '../lib/supercompute';
import { PRESENCE_CHAIN, PRESENCE_CHAIN_ID } from '../lib/presence-chain';
import { PRESENCE_EVENT, proposedSchemaUID } from '../lib/eas';
import {
  ERC6551_ACCOUNT_IMPLEMENTATION,
  ERC6551_REGISTRY,
  PILLARS,
  derivePassport,
  emptyPassport,
  immersionAccount,
  IMMERSION_CHAIN_ID,
  IMMERSION_TOKEN_CONTRACT,
} from '../lib/immersion';
import {
  PERMISSION_KEYS,
  ROLES,
  grantedPermissions,
  hasPermission,
  roleById,
} from '../lib/roles';
import { EmbassyCharter } from './EmbassyCharter';

/* ── the desk's own vocabulary ────────────────────────────────────────────── */

const TABS = [
  { id: 'passport', label: 'Passport' },
  { id: 'identity', label: 'Identity' },
  { id: 'charter', label: 'Charter' },
  { id: 'credentials', label: 'Credentials' },
  { id: 'postings', label: 'Postings' },
  { id: 'interop', label: 'Interop' },
  { id: 'request', label: 'Request' },
] as const;

type TabId = (typeof TABS)[number]['id'];

const TEXT_KEYS = [
  { key: 'description', label: 'Description' },
  { key: 'url', label: 'URL' },
  { key: 'com.twitter', label: 'X / Twitter' },
  { key: 'com.github', label: 'GitHub' },
  { key: 'avatar', label: 'Avatar record' },
] as const;

const POSTING_KEY = 'supercompute-webos:embassy.postings';
const REQUEST_KEY = 'supercompute-webos:embassy.requests';

interface QueuedPosting {
  id: number;
  no: string;
  title: string;
  audience: string;
  date: string;
}

/* ── local-only stores ────────────────────────────────────────────────────── */

/**
 * Both stores below are `localStorage`. The desk says so wherever it shows one:
 * "queued in this browser" is a different claim from "published", and the
 * difference is the whole point of a ledger.
 */
function readLocal<T>(key: string, fallback: T): T {
  try {
    const raw = window.localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch {
    return fallback;
  }
}

function writeLocal(key: string, value: unknown): void {
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* storage full or blocked — the UI still reflects in-memory state */
  }
}

/* ── small parts ──────────────────────────────────────────────────────────── */

function Field({ k, children }: { k: string; children: React.ReactNode }) {
  return (
    <>
      <dt>{k}</dt>
      <dd>{children}</dd>
    </>
  );
}

function TextRecord({
  name,
  recordKey,
  label,
}: {
  name: string | undefined;
  recordKey: string;
  label: string;
}) {
  const { data, isLoading } = useEnsText({
    name,
    key: recordKey,
    chainId: mainnet.id,
  });
  return (
    <div className="row">
      <span className="row__k">{label}</span>
      <span className="row__v">
        {!name ? (
          <span className="tag tag--muted">no ENS name</span>
        ) : isLoading ? (
          <span className="tag tag--muted">reading…</span>
        ) : data ? (
          data
        ) : (
          <span className="tag tag--muted">unset</span>
        )}
      </span>
    </div>
  );
}

/** One attestation, as a stamp in the book. */
function Stamp({
  title,
  primary,
  secondary,
  voided,
  onClick,
}: {
  title: string;
  primary: string;
  secondary: string;
  voided?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      className={voided ? 'stamp stamp--void' : 'stamp'}
      style={{ textAlign: 'left', cursor: 'pointer' }}
      onClick={onClick}
    >
      <img
        className="pixel stamp__seal"
        src={voided ? '/pixel/seal-revoked.png' : '/pixel/seal-issued.png'}
        width={24}
        height={24}
        alt=""
        aria-hidden="true"
      />
      <span className="stamp__t">{title}</span>
      <span className="stamp__m">{primary}</span>
      <span className="stamp__m">{secondary}</span>
      {voided && <span className="stamp__m">REVOKED</span>}
    </button>
  );
}

/* ── the desk ─────────────────────────────────────────────────────────────── */

export function DigitalEmbassy() {
  const w = useWallet();
  const [tab, setTab] = useState<TabId>('passport');
  const [roles, setRoles] = useState<string[]>([]);
  const [stampChain, setStampChain] = useState<number>(8453);
  const [selectedUid, setSelectedUid] = useState<string | null>(null);

  const [postings, setPostings] = useState<QueuedPosting[]>(() =>
    readLocal<QueuedPosting[]>(POSTING_KEY, []),
  );
  const [requests, setRequests] = useState<number>(() =>
    readLocal<number>(REQUEST_KEY, 0),
  );

  useEffect(() => writeLocal(POSTING_KEY, postings), [postings]);
  useEffect(() => writeLocal(REQUEST_KEY, requests), [requests]);

  const granted = useMemo(() => grantedPermissions(roles), [roles]);

  /* ── attestations: the stamps ──────────────────────────────────────────── */

  const { read: stamps, isPending: stampsPending, refetch: refetchStamps } =
    useAttestationsReceived(stampChain, w.address, { take: 24 });
  const { read: stampTotal } = useAttestationCount(stampChain, w.address);

  const stampCount =
    stampTotal?.status === 'ok' ? stampTotal.data : undefined;

  /* Is the canon PresenceEvent schema actually registered yet? */
  const presenceUid = useMemo(() => proposedSchemaUID(PRESENCE_EVENT), []);
  const { read: presenceSchema } = useSchemaRegistration(
    PRESENCE_CHAIN_ID,
    presenceUid,
  );

  /* ── the container (ERC-6551) ──────────────────────────────────────────── */

  const tokenContract = (
    import.meta.env.VITE_IMMERSION_TOKEN_CONTRACT || IMMERSION_TOKEN_CONTRACT
  ) as `0x${string}`;

  const container = useMemo(() => {
    if (!/^0x[0-9a-fA-F]{40}$/.test(tokenContract)) return null;
    try {
      return immersionAccount(tokenContract, 1n, IMMERSION_CHAIN_ID);
    } catch {
      return null;
    }
  }, [tokenContract]);

  const passport = useMemo(
    () =>
      container
        ? derivePassport({
            tokenContract,
            tokenId: 1n,
            ensName: w.ensName ?? null,
            roles,
          })
        : emptyPassport(w.ensName ?? null),
    [container, tokenContract, w.ensName, roles],
  );

  /* ── machine zone ──────────────────────────────────────────────────────── */

  const chainLabel =
    stampChain === 8453 ? 'BASE' : stampChain === 84532 ? 'BASE-SEP' : `CHAIN-${stampChain}`;

  const mrz = useMemo(() => {
    const handlePart = (w.ensName ?? w.shortAddress ?? 'GUEST').replace(
      /\.eth$/i,
      '',
    );
    const line1 = `P<SCP${mrzChars(handlePart, 39)}`;

    const docNo = w.address
      ? mrzChars(w.address.slice(2), 9)
      : 'UNISSUED<';
    const check = icaoCheckDigit(docNo);
    const tail = `${docNo}${check}SCP${mrzChars(chainLabel, 8)}${String(
      stampCount ?? 0,
    ).padStart(4, '0')}`;
    const line2 = `${tail}${'<'.repeat(Math.max(0, 44 - tail.length))}`;

    return { line1, line2: line2.slice(0, 44) };
  }, [w.ensName, w.shortAddress, w.address, chainLabel, stampCount]);

  const initials = (w.ensName ?? w.shortAddress ?? 'GUEST')
    .replace(/^0x/, '')
    .replace(/[^a-zA-Z0-9]/g, '')
    .slice(0, 2)
    .toUpperCase();

  /* ── postings ──────────────────────────────────────────────────────────── */

  const [draftTitle, setDraftTitle] = useState('');
  const [draftAudience, setDraftAudience] = useState('operators');
  const [draftError, setDraftError] = useState<string | null>(null);

  const queuePosting = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      const title = draftTitle.trim();
      if (title.length < 6) {
        setDraftError('Subject is required — at least 6 characters.');
        return;
      }
      setDraftError(null);
      setPostings((prev) => [
        {
          id: Date.now(),
          no: `Q-${String(prev.length + 1).padStart(3, '0')}`,
          title,
          audience: draftAudience,
          date: new Date().toISOString().slice(0, 10),
        },
        ...prev,
      ]);
      setDraftTitle('');
    },
    [draftTitle, draftAudience],
  );

  /* ── the credential reader ─────────────────────────────────────────────── */

  const rows =
    stamps?.status === 'ok' ? stamps.data : ([] as const);
  const selected = rows.find((r) => r.uid === selectedUid) ?? null;

  const readNote = (read: EasRead<unknown> | undefined): React.ReactNode => {
    if (!read) return <span className="tag tag--muted">idle</span>;
    switch (read.status) {
      case 'no-subject':
        return <span className="tag tag--muted">{read.reason}</span>;
      case 'no-indexer':
        return <span className="tag tag--warn">{read.reason}</span>;
      case 'error':
        return (
          <span className="tag tag--err">
            {read.reason} — {read.endpoint.replace(/^https:\/\//, '')}
          </span>
        );
      default:
        return null;
    }
  };

  return (
    <div className="dsp page">
      {/* ── masthead — crest · eyebrow · h1 · status chips ───────────────── */}
      <header className="page__head">
        <img
          className="page__crest"
          src="/logos/dock-embassy.png"
          width={40}
          height={40}
          alt=""
          aria-hidden="true"
        />
        <div className="page__titles">
          <p className="eyebrow">embassy · self-custody · presence</p>
          <h1 className="h1">What we say outward</h1>
        </div>
        <div className="page__chips">
          <span className="badge badge-admin">
            {!w.address
              ? 'no holder'
              : stampCount !== undefined
                ? `${stampCount} stamp${stampCount === 1 ? '' : 's'}`
                : stampsPending
                  ? 'reading…'
                  : 'count unavailable'}
          </span>
        </div>
      </header>

      <div className="tabs page__tabs" role="tablist" aria-label="Embassy modules">
        {TABS.map((t) => (
          <button
            key={t.id}
            role="tab"
            type="button"
            aria-selected={tab === t.id}
            tabIndex={tab === t.id ? 0 : -1}
            onClick={() => setTab(t.id)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* ══ PASSPORT ═══════════════════════════════════════════════════════ */}
      <section className="panel" hidden={tab !== 'passport'}>
        <div className="passport">
          <div className="passport__head">
            <div>
              <div className="passport__authority">
                Supercompute · Digital Embassy
              </div>
              <div className="passport__type">
                Type SCP · sovereign self-custody pass
              </div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div className="passport__type">code</div>
              <div className="micro micro--ink">SCP</div>
            </div>
          </div>

          <div className="passport__body">
            <figure
              className={
                w.ensAvatar
                  ? 'passport__photo'
                  : 'passport__photo passport__photo--blank'
              }
            >
              {w.ensAvatar ? (
                <img src={w.ensAvatar} alt={`${w.handle} avatar`} />
              ) : (
                <img
                  className="pixel"
                  src="/pixel/portrait-blank.png"
                  width={48}
                  height={48}
                  alt=""
                  aria-hidden="true"
                />
              )}
              <figcaption>
                {w.ensAvatar ? 'holder photo · ENS avatar' : 'no avatar record'}
              </figcaption>
            </figure>

            <dl className="passport__fields">
              <Field k="Holder">{w.handle}</Field>
              <Field k="Address">
                {w.address ? (
                  w.address
                ) : (
                  <span className="tag tag--muted">not connected</span>
                )}
              </Field>
              <Field k="Issuing state">{ORG_ENS_NAME}</Field>
              <Field k="Chain">
                {w.chainId === undefined
                  ? '—'
                  : w.chainId === mainnet.id
                    ? 'Ethereum (1)'
                    : w.chainId === 8453
                      ? 'Base (8453)'
                      : w.chainId === 84532
                        ? 'Base Sepolia (84532)'
                        : `Chain ${w.chainId}`}
              </Field>
              <Field k="Container">
                {passport.account ? (
                  <>
                    <span className="mono mono--ink">{passport.account}</span>
                    <br />
                    <span className="sub">
                      token #{passport.tokenId?.toString() ?? '—'} ·{' '}
                      {passport.chainName}
                    </span>
                  </>
                ) : (
                  <span className="tag tag--warn">
                    no token — nothing minted yet
                  </span>
                )}
              </Field>
              <Field k="Stamps">
                {stampCount === undefined ? (
                  <span className="tag tag--muted">
                    {stampTotal?.status === 'error'
                      ? 'indexer unreachable'
                      : 'not read'}
                  </span>
                ) : (
                  <>
                    {stampCount} ·{' '}
                    <span className="tag tag--muted">{chainLabel}</span>
                  </>
                )}
              </Field>
              <Field k="Minted">
                <span className={passport.minted ? 'tag tag--ok' : 'tag tag--warn'}>
                  {passport.minted ? 'yes' : 'no'}
                </span>
              </Field>
            </dl>
          </div>

          <div className="passport__mrz">
            <div>{mrz.line1}</div>
            <div>{mrz.line2}</div>
            <div style={{ letterSpacing: 0, marginTop: 6 }}>
              machine zone derived from the fields above · check character{' '}
              <b>ICAO 9303</b> (7-3-1). Not a travel document.
            </div>
          </div>
        </div>

        {/* stamps */}
        <div className="page__bar">
          <div className="grow">
            <p className="micro micro--ink">stamps — attestations held</p>
            <p className="cap">
              One stamp per EAS attestation written to this address. The desk does
              not draw a stamp it cannot find on chain.
            </p>
          </div>
          <div className="row" style={{ gap: 6 }}>
            <button
              type="button"
              className={
                stampChain === 8453 ? 'btn btn--gold' : 'btn btn--ghost'
              }
              onClick={() => setStampChain(8453)}
            >
              Base
            </button>
            <button
              type="button"
              className={
                stampChain === 84532 ? 'btn btn--gold' : 'btn btn--ghost'
              }
              onClick={() => setStampChain(84532)}
            >
              Sepolia
            </button>
          </div>
        </div>

        {rows.length > 0 ? (
          <div className="stamps">
            {rows.map((s) => (
              <Stamp
                key={s.uid}
                title={s.schemaName ?? shortUid(s.schemaId, 8, 4)}
                primary={unixToDate(s.time)}
                secondary={shortUid(s.uid, 8, 4)}
                voided={s.revoked}
                onClick={() => {
                  setSelectedUid(s.uid);
                  setTab('credentials');
                }}
              />
            ))}
          </div>
        ) : (
          <div className="empty">
            <img
              className="pixel empty__mark"
              src="/pixel/empty-frame.png"
              width={32}
              height={32}
              alt=""
              aria-hidden="true"
            />
            {stampTotal?.status === 'no-subject' ? (
              <>Connect a wallet — a passport has no stamps until it has a holder.</>
            ) : stampTotal?.status === 'error' || stamps?.status === 'error' ? (
              <>The EAS indexer could not be reached, so this page cannot claim the
                passport is empty. Retry before reading anything into it.</>
            ) : (
              <>
                No attestations held on {chainLabel}. That is a real zero, counted
                by the indexer — not a failed read.
              </>
            )}
          </div>
        )}

        {stampTotal?.status === 'error' && (
          <div className="notice notice--warn">
            <span className="tick" aria-hidden="true" />
            <span>
              <strong>Indexer read failed.</strong> {stampTotal.reason} ·{' '}
              {stampTotal.endpoint.replace(/^https:\/\//, '')}
              <button
                type="button"
                className="btn btn--ghost"
                style={{ marginLeft: 8 }}
                onClick={() => void refetchStamps()}
              >
                Retry
              </button>
            </span>
          </div>
        )}

        <p className="cap">
          Counts and stamps are read from the {chainLabel} EAS indexer
          {' '}
          <span className="mono">{indexerFor(stampChain)?.replace(/^https:\/\//, '')}</span>
          {' '}— an index of the chain, not the chain. Zero here means the indexer
          answered zero, which is the strongest claim available without reading
          every block.
        </p>
      </section>

      {/* ══ IDENTITY ═══════════════════════════════════════════════════════ */}
      <section className="panel" hidden={tab !== 'identity'}>
        <div className="row" style={{ alignItems: 'flex-start' }}>
          {w.ensAvatar ? (
            <img
              className="passport__photo"
              style={{ width: 56, height: 56 }}
              src={w.ensAvatar}
              alt={`${w.handle} avatar`}
            />
          ) : (
            <div className="monogram" aria-hidden="true">
              {initials || '◈'}
            </div>
          )}
          <div className="stack grow" style={{ gap: 2 }}>
            <span className="eyebrow">immersion session</span>
            <span className="h2">{w.handle}</span>
            {w.shortAddress && <span className="cap">{w.shortAddress}</span>}
            <span className="row" style={{ gap: 4 }}>
              {w.isConnected ? (
                <>
                  <span className="badge badge-live">
                    connected
                  </span>
                  <span className="badge badge-beta">
                    {w.chainId === mainnet.id
                      ? 'mainnet'
                      : w.chainId === 8453
                        ? 'base'
                        : w.chainId === 84532
                          ? 'base sepolia'
                          : `chain ${w.chainId ?? '?'}`}
                  </span>
                </>
              ) : (
                <span className="badge badge-claude">self-custody · locked</span>
              )}
            </span>
          </div>
        </div>

        <div className="row">
          {w.isConnected ? (
            <button type="button" className="btn" onClick={w.disconnect}>
              Disconnect
            </button>
          ) : (
            <button
              type="button"
              className="btn btn--gold"
              onClick={w.connect}
              disabled={w.isConnecting || w.availableConnectors.length === 0}
            >
              {w.isConnecting ? 'Connecting…' : 'Connect wallet'}
            </button>
          )}
          {w.isConnected && !w.onDefaultChain && (
            <button type="button" className="btn" onClick={w.switchToDefaultChain}>
              Switch to Presence chain
            </button>
          )}
        </div>
        {w.error && <p className="notice notice--warn">{w.error}</p>}

        <p className="eyebrow">ens — ethereum mainnet</p>
        <div className="term">
          <div className="row">
            <span className="row__k">Primary name</span>
            <span className="row__v">
              {!w.isConnected ? (
                <span className="tag tag--muted">—</span>
              ) : w.ensNameLoading ? (
                <span className="tag tag--muted">resolving…</span>
              ) : w.ensName ? (
                w.ensName
              ) : (
                <span className="tag tag--warn">no reverse record</span>
              )}
            </span>
          </div>
          <div className="row">
            <span className="row__k">Address</span>
            <span className="row__v">
              {w.address ?? <span className="tag tag--muted">—</span>}
            </span>
          </div>
          {TEXT_KEYS.map((t) => (
            <TextRecord
              key={t.key}
              name={w.ensName}
              recordKey={t.key}
              label={t.label}
            />
          ))}
        </div>

        <p className="eyebrow">immersion nft — erc-6551 container</p>
        <div className="term">
          <dl className="kv">
            <dt>registry</dt>
            <dd className="mono">{ERC6551_REGISTRY}</dd>
            <dt>implementation</dt>
            <dd className="mono">{ERC6551_ACCOUNT_IMPLEMENTATION}</dd>
            <dt>token contract</dt>
            <dd className="mono">{tokenContract}</dd>
            <dt>derived account</dt>
            <dd className="mono">
              {container ?? (
                <span className="tag tag--muted">
                  nothing minted yet
                </span>
              )}
            </dd>
            <dt>minted</dt>
            <dd>
              <span className="tag tag--warn">no</span>
            </dd>
          </dl>
        </div>
        <p className="cap">
          Registry and implementation are live on Base and Base Sepolia, and the
          derivation is checked against the deployed registry. The collection is
          deployed but nothing is minted, so there is no token to bind and the
          account exists only as a derived address — the row above is honest about
          that rather than claiming the account is live.
        </p>

        <p className="eyebrow">roles — {ROLES.length} designations</p>
        <div className="row" style={{ flexWrap: 'wrap' }}>
          {ROLES.map((r) => (
            <button
              key={r.id}
              type="button"
              className="btn"
              title={`${r.purpose} · ${r.ensPattern}`}
              style={
                roles.includes(r.id)
                  ? { borderColor: 'var(--ui-accent-3)', color: 'var(--ui-accent-3)' }
                  : undefined
              }
              onClick={() =>
                setRoles((prev) =>
                  prev.includes(r.id)
                    ? prev.filter((x) => x !== r.id)
                    : [...prev, r.id],
                )
              }
            >
              {r.label}
            </button>
          ))}
        </div>
        <div className="term">
          <dl className="kv">
            <dt>held</dt>
            <dd>
              {roles.length === 0 ? (
                <span className="tag tag--muted">none</span>
              ) : (
                roles.map((id) => roleById(id)?.label).join(', ')
              )}
            </dd>
            <dt>capabilities</dt>
            <dd>
              {granted.length} / {PERMISSION_KEYS.length}
            </dd>
            <dt>can govern</dt>
            <dd>{hasPermission(roles, 'GOV_PROPOSE') ? 'yes' : 'no'}</dd>
            <dt>can spend</dt>
            <dd>{hasPermission(roles, 'FISC_SPEND') ? 'yes' : 'no'}</dd>
            <dt>can sign for org</dt>
            <dd>{hasPermission(roles, 'WALLET_SIGN_ORG') ? 'yes' : 'no'}</dd>
          </dl>
        </div>
        {granted.length > 0 && (
          <div className="row" style={{ flexWrap: 'wrap', gap: 3 }}>
            {granted.map((k) => (
              <span key={k} className="badge badge-admin">
                {k}
              </span>
            ))}
          </div>
        )}
        <div className="notice notice--brass">
          <span className="tick" aria-hidden="true" />
          <span>
            <strong>Proposed · not enforced.</strong> No roles contract exists in
            any Supercompute repo, so these bits are a local predicate, not an
            access check. Nothing on-chain consults them.
          </span>
        </div>

        <p className="eyebrow">architecture — four pillars</p>
        <div className="term">
          {PILLARS.map((p) => (
            <div className="row" key={p.id} style={{ alignItems: 'flex-start' }}>
              <span className="row__k">{p.label}</span>
              <span className="row__v">
                {p.modules.map((m) => (
                  <span
                    key={m.label}
                    className={m.pending ? 'tag tag--warn' : 'tag tag--muted'}
                    style={{ marginRight: 3 }}
                  >
                    {m.label}
                  </span>
                ))}
              </span>
            </div>
          ))}
        </div>
      </section>

      {/* ══ CHARTER ════════════════════════════════════════════════════════ */}
      <section className="panel" hidden={tab !== 'charter'}>
        <EmbassyCharter />
      </section>

      {/* ══ CREDENTIALS ════════════════════════════════════════════════════ */}
      <section className="panel" hidden={tab !== 'credentials'}>
        <div className="row row--between">
          <div className="grow">
            <p className="micro micro--ink">credential ledger</p>
            <p className="cap">
              Every attestation this address holds on {chainLabel}, newest first.
              Select a row to read it in full.
            </p>
          </div>
          <span className="mono">
            {stampsPending && !rows.length
              ? 'reading…'
              : `${rows.length} row${rows.length === 1 ? '' : 's'}`}
          </span>
        </div>

        {readNote(stamps) && <div className="row">{readNote(stamps)}</div>}

        {rows.length > 0 && (
          <div className="tblwrap">
            <table className="tbl">
              <thead>
                <tr>
                  <th>No.</th>
                  <th>Type</th>
                  <th>Attester</th>
                  <th>Standard</th>
                  <th>State</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr
                    key={r.uid}
                    tabIndex={0}
                    role="button"
                    aria-label={`Read attestation ${r.uid}`}
                    style={{ cursor: 'pointer' }}
                    onClick={() => setSelectedUid(r.uid)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        setSelectedUid(r.uid);
                      }
                    }}
                  >
                    <td className="mono">
                      C-{String(i + 1).padStart(2, '0')}
                    </td>
                    <td>
                      {r.schemaName ?? 'unnamed schema'}
                      <span className="sub">{shortUid(r.uid, 12, 6)}</span>
                    </td>
                    <td className="mono">{shortUid(r.attester, 8, 6)}</td>
                    <td className="mono">{shortUid(r.schemaId, 8, 4)}</td>
                    <td>
                      {r.revoked ? (
                        <span className="tag tag--err">revoked</span>
                      ) : (
                        <span className="badge badge-live">
                          issued
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {selected ? (
          <div className="term">
            <div className="row">
              <span className="monogram" aria-hidden="true">
                {(selected.schemaName ?? 'EA')
                  .replace(/[^a-zA-Z]/g, '')
                  .slice(0, 2)
                  .toUpperCase()}
              </span>
              <div className="grow">
                <p className="eyebrow">
                  {selected.schemaName ?? 'attestation'}
                </p>
                <p className="mono mono--ink">
                  {selected.schemaId}
                </p>
              </div>
            </div>
            <dl className="kv" style={{ marginTop: 10 }}>
              <dt>issuer</dt>
              <dd className="mono">{selected.attester}</dd>
              <dt>recipient</dt>
              <dd className="mono">{selected.recipient}</dd>
              <dt>chain</dt>
              <dd className="mono">
                {chainLabel} ({stampChain})
              </dd>
              <dt>uid</dt>
              <dd className="mono">{selected.uid}</dd>
              <dt>issued</dt>
              <dd className="mono">{unixToDate(selected.time)}</dd>
              <dt>state</dt>
              <dd>
                {selected.revoked ? (
                  <span className="tag tag--err">revoked</span>
                ) : (
                  <span className="tag tag--ok">live</span>
                )}
              </dd>
              <dt>ref uid</dt>
              <dd className="mono">
                {/^0x0{64}$/.test(selected.refUID)
                  ? 'none'
                  : selected.refUID}
              </dd>
            </dl>

            {selected.fields.length > 0 && (
              <>
                <p className="eyebrow" style={{ marginTop: 12 }}>
                  decoded payload
                </p>
                <dl className="kv">
                  {selected.fields.map((f) => (
                    <Field key={f.name} k={f.name}>
                      <span className="mono mono--ink">{f.value}</span>
                    </Field>
                  ))}
                </dl>
              </>
            )}

            <div className="row row--between foot">
              <button
                type="button"
                className="btn btn--ghost"
                onClick={() => {
                  void navigator.clipboard?.writeText(selected.uid);
                }}
              >
                Copy uid
              </button>
              <span className="badge badge-admin">read from the indexer</span>
            </div>
          </div>
        ) : (
          <div className="empty">
            {rows.length > 0
              ? 'Select a row to read the credential in full.'
              : 'No attestation to read.'}
          </div>
        )}

        <p className="eyebrow">specified credential types</p>
        <div className="tblwrap">
          <table className="tbl">
            <thead>
              <tr>
                <th>Type</th>
                <th>Subject</th>
                <th>Standard</th>
                <th>State</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>operator-access</td>
                <td>operator · {ORG_ENS_NAME}</td>
                <td className="mono">ACP key</td>
                <td>
                  <img
                    className="seal-inline"
                    src="/pixel/seal-pending.png"
                    width={20}
                    height={20}
                    alt=""
                    aria-hidden="true"
                  />
                  <span className="tag tag--warn">draft</span>
                </td>
              </tr>
              <tr>
                <td>vendor-onboarding</td>
                <td>vendor · pending</td>
                <td className="mono">
                  {proposedSchemaUID(PRESENCE_EVENT).slice(0, 10)}…
                </td>
                <td>
                  <img
                    className="seal-inline"
                    src="/pixel/seal-pending.png"
                    width={20}
                    height={20}
                    alt=""
                    aria-hidden="true"
                  />
                  <span className="tag tag--warn">draft</span>
                </td>
              </tr>
              <tr>
                <td>event-attendance</td>
                <td>PresenceEvent</td>
                <td className="mono">{PRESENCE_EVENT.key}</td>
                <td>
                  {presenceSchema?.status === 'ok' ? (
                    presenceSchema.data ? (
                      <span className="badge badge-live">
                        schema live
                      </span>
                    ) : (
                      <span className="tag tag--warn">schema unregistered</span>
                    )
                  ) : (
                    <span className="tag tag--muted">
                      {presenceSchema?.status === 'error'
                        ? 'registry unreachable'
                        : 'checking…'}
                    </span>
                  )}
                </td>
              </tr>
              <tr>
                <td>school-completion</td>
                <td>school cohort</td>
                <td className="mono">{PRESENCE_EVENT.key}</td>
                <td>
                  <span className="tag tag--warn">draft</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div className="notice notice--brass">
          <span className="tick" aria-hidden="true" />
          <span>
            <strong>A draft is a promise.</strong> Only the rows in the ledger above
            exist on chain. The proposed PresenceEvent schema UID is{' '}
            <span className="mono">{shortUid(presenceUid, 14, 8)}</span> on{' '}
            {PRESENCE_CHAIN.chainName}; asked directly, the registry answers{' '}
            {presenceSchema?.status === 'ok'
              ? presenceSchema.data
                ? 'registered.'
                : 'nothing at that UID.'
              : 'unknown.'}
          </span>
        </div>
      </section>

      {/* ══ POSTINGS ═══════════════════════════════════════════════════════ */}
      <section className="panel" hidden={tab !== 'postings'}>
        <div className="grid4">
          <div className="stat-tile">
            <p className="eyebrow">published</p>
            <p className="val">0</p>
            <p className="sub">Notices on the record.</p>
          </div>
          <div className="stat-tile">
            <p className="eyebrow">open</p>
            <p className="val">0</p>
            <p className="sub">Consultations taking replies.</p>
          </div>
          <div className="stat-tile">
            <p className="eyebrow">queue</p>
            <p className="val">{postings.length}</p>
            <p className="sub">Held in this browser.</p>
          </div>
          <div className="stat-tile">
            <p className="eyebrow">last</p>
            <p className="val" style={{ fontSize: 15 }}>
              {postings[0]?.date ?? '—'}
            </p>
            <p className="sub">Latest posting.</p>
          </div>
        </div>

        <div className="row row--between">
          <p className="micro micro--ink">the record</p>
          <span className="badge badge-admin">nothing on chain</span>
        </div>

        {postings.length > 0 ? (
          <div className="tblwrap">
            <table className="tbl">
              <thead>
                <tr>
                  <th>No.</th>
                  <th>Posting</th>
                  <th>Audience</th>
                  <th>State</th>
                  <th className="r">Date</th>
                </tr>
              </thead>
              <tbody>
                {postings.map((p) => (
                  <tr key={p.id}>
                    <td className="mono">{p.no}</td>
                    <td>
                      {p.title}
                      <span className="sub">Queued in this browser</span>
                    </td>
                    <td>{p.audience}</td>
                    <td>
                      <span className="tag tag--warn">queued</span>
                    </td>
                    <td className="r mono">{p.date}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="empty">
            The record is empty. Nothing has been published, and this desk will not
            show a notice that was never issued.
          </div>
        )}

        <form className="term stack" onSubmit={queuePosting} noValidate>
          <div className="row row--between">
            <p className="micro micro--ink">new posting</p>
            <span className="badge badge-admin">held in this browser</span>
          </div>
          <div>
            <label className="lbl micro" htmlFor="pTitle">
              Subject
            </label>
            <input
              className="field"
              id="pTitle"
              type="text"
              placeholder="what the notice says"
              value={draftTitle}
              onChange={(e) => setDraftTitle(e.target.value)}
            />
          </div>
          <div>
            <label className="lbl micro" htmlFor="pAudience">
              Audience
            </label>
            <select
              className="field"
              id="pAudience"
              value={draftAudience}
              onChange={(e) => setDraftAudience(e.target.value)}
            >
              <option>operators</option>
              <option>vendors</option>
              <option>members</option>
              <option>the public record</option>
            </select>
          </div>
          {draftError && <p className="err">{draftError}</p>}
          <div className="row">
            <button className="btn btn--gold" type="submit">
              Queue notice
            </button>
            {postings.length > 0 && (
              <button
                className="btn btn--ghost"
                type="button"
                onClick={() => setPostings([])}
              >
                Clear queue
              </button>
            )}
          </div>
        </form>

        <div className="notice notice--brass">
          <span className="tick" aria-hidden="true" />
          <span>
            <strong>No publication path yet.</strong> Notices live in this browser's
            storage only — no EAS write, no relay, no DISPATCH attestation is sent.
            The dispatch schema (NEWSDESK_DISPATCH) is a Supercompute proposal, not
            a canon Presence schema.
          </span>
        </div>
      </section>

      {/* ══ INTEROP ════════════════════════════════════════════════════════ */}
      <section className="panel" hidden={tab !== 'interop'}>
        <p className="micro micro--ink">rails this desk answers to</p>
        <div className="tblwrap">
          <table className="tbl">
            <thead>
              <tr>
                <th>Rail</th>
                <th>Purpose</th>
                <th>State</th>
                <th>Note</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>ENS</td>
                <td>Identity, avatar, text records</td>
                <td>
                  <span className="badge badge-live">
                    linked
                  </span>
                </td>
                <td className="mono">read on mainnet</td>
              </tr>
              <tr>
                <td>EAS</td>
                <td>Attestations — stamps and credentials</td>
                <td>
                  <span className="badge badge-live">
                    linked
                  </span>
                </td>
                <td className="mono">read via indexer</td>
              </tr>
              <tr>
                <td>Base</td>
                <td>Settlement layer</td>
                <td>
                  <span className="badge badge-live">
                    linked
                  </span>
                </td>
                <td className="mono">
                  {w.chainId === 8453 || w.chainId === 84532
                    ? 'wallet on Base'
                    : 'self-custody'}
                </td>
              </tr>
              <tr>
                <td>ACP · Virtuals</td>
                <td>Agent commerce, jobs and offerings</td>
                <td>
                  <span className="tag tag--warn">not linked</span>
                </td>
                <td className="mono">on-chain ACP pending</td>
              </tr>
              <tr>
                <td>Farcaster</td>
                <td>Social channel</td>
                <td>
                  <span className="tag tag--warn">not linked</span>
                </td>
                <td className="mono">connect on the Channel desk</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div className="term row row--between">
          <div className="grow">
            <p className="micro micro--ink">Capability manifest</p>
            <p className="cap">
              Publish what this desk can be asked to do. Local flag — nothing is
              announced anywhere.
            </p>
          </div>
          <button
            type="button"
            className="switch"
            role="switch"
            aria-checked={false}
            aria-label="Publish capability manifest"
          >
            <span className="switch__track">
              <span className="switch__knob" />
            </span>
          </button>
        </div>
      </section>

      {/* ══ REQUEST ════════════════════════════════════════════════════════ */}
      <section className="panel" hidden={tab !== 'request'}>
        <div className="grid2">
          <div className="stack">
            <p className="micro micro--ink">ask for a credential</p>
            <form
              className="stack"
              onSubmit={(e) => {
                e.preventDefault();
                setRequests((n) => n + 1);
              }}
            >
              <div>
                <label className="lbl micro" htmlFor="rOrg">
                  Organisation
                </label>
                <input
                  className="field"
                  id="rOrg"
                  name="org"
                  type="text"
                  placeholder="who is asking"
                  required
                />
              </div>
              <div>
                <label className="lbl micro" htmlFor="rMail">
                  Contact
                </label>
                <input
                  className="field"
                  id="rMail"
                  name="email"
                  type="email"
                  placeholder="name@domain"
                  required
                />
              </div>
              <div>
                <label className="lbl micro" htmlFor="rScope">
                  Scope
                </label>
                <select className="field" id="rScope" name="scope">
                  <option>operator access</option>
                  <option>vendor onboarding</option>
                  <option>publishing rights</option>
                  <option>attestation rights</option>
                </select>
              </div>
              <button className="btn btn--gold" type="submit">
                Log the request
              </button>
            </form>
            <p className="cap">
              {requests === 0
                ? 'No request logged in this browser.'
                : `${requests} request${requests === 1 ? '' : 's'} logged in this browser — send it from the Embassy mailbox to make it real.`}
            </p>
          </div>

          <div className="stack">
            <p className="micro micro--ink">what a credential carries</p>
            <ul className="rows">
              <li className="item">
                <span className="tick" aria-hidden="true" />
                <span>
                  Type · <span className="mono">what it certifies</span>
                </span>
              </li>
              <li className="item">
                <span className="tick" aria-hidden="true" />
                <span>
                  Subject · <span className="mono">who it names</span>
                </span>
              </li>
              <li className="item">
                <span className="tick" aria-hidden="true" />
                <span>
                  Issuer · <span className="mono">Supercompute, on Base</span>
                </span>
              </li>
              <li className="item">
                <span className="tick" aria-hidden="true" />
                <span>
                  State · <span className="mono">draft, issued or revoked</span>
                </span>
              </li>
            </ul>
            <p className="cap">
              A draft is a promise. Only an issued credential is worth showing to
              anyone.
            </p>
          </div>
        </div>
      </section>

      <p className="page__foot">
        Organisation {ORG_ENS_NAME} · governance via Aragon. The Embassy is the outward face: one
        pass, stamps in the book, and one line to each rail the desk answers to.
      </p>
    </div>
  );
}
